import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Table, TableHead, TableBody, TableRow,
    TableCell, MenuItem, TextField, CircularProgress, Paper,
    Button
} from '@mui/material';
import { TaxService } from '../../services/tax.service';
import { IEmployeeTaxSummary, ITaxYear } from '../../interfaces/tax.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';

const EmployeeTaxList: React.FC = () => {
    const { showError } = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [taxYears, setTaxYears] = useState<ITaxYear[]>([]);
    const [selectedYearId, setSelectedYearId] = useState('');
    const [summaries, setSummaries] = useState<IEmployeeTaxSummary[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        TaxService.getTaxYears(institutionId)
            .then(years => {
                setTaxYears(years);
                const active = years.find(y => y.isActive) ?? years[0];
                if (active) setSelectedYearId(active.id!);
            })
            .catch(() => showError('Failed to load tax years'));
    }, [institutionId]);

    useEffect(() => {
        if (!selectedYearId) return;
        setLoading(true);
        TaxService.getEmployeeTaxSummary(selectedYearId, institutionId)
            .then(setSummaries)
            .catch(() => showError('Failed to load employee tax summary'))
            .finally(() => setLoading(false));
    }, [selectedYearId, institutionId]);

    const totalYtdTax = summaries.reduce((a, s) => a + s.taxDeductedYtd, 0);
    const totalProjected = summaries.reduce((a, s) => a + s.projectedRemainingTax, 0);

    const downloadCertificate = async (userId: string) => {
        try {
            const blob = await TaxService.downloadCertificatePdf(selectedYearId, userId, institutionId);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `tax-certificate-${userId}.pdf`; a.click();
            URL.revokeObjectURL(url);
        } catch { showError('Failed to download certificate'); }
    };

    return (
        <Box data-help-page="tax.employeeTax.page" p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">Employee Tax Summary</Typography>
                <TextField data-help="tax.employeeTax.year" select size="small" label="Tax Year" value={selectedYearId}
                    onChange={e => setSelectedYearId(e.target.value)} sx={{ width: 160 }}>
                    {taxYears.map(y => <MenuItem key={y.id} value={y.id}>{y.label}</MenuItem>)}
                </TextField>
            </Box>

            <Box display="flex" gap={2} mb={3}>
                {[
                    { label: 'Total Employees', value: summaries.length },
                    { label: 'Total Tax Deducted YTD', value: totalYtdTax.toLocaleString() },
                    { label: 'Projected Remaining Tax', value: totalProjected.toLocaleString() },
                ].map(card => (
                    <Paper key={card.label} sx={{ p: 2, flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                        <Typography variant="h6" fontWeight={700}>{card.value}</Typography>
                    </Paper>
                ))}
            </Box>

            {loading ? <CircularProgress /> : (
                <Paper>
                    <ScrollableTable>
                        <Table data-help-section="tax.employeeTax.table" size="small">
                            <TableHead>
                                <TableRow sx={{ background: '#f1f5f9' }}>
                                    <TableCell data-help="tax.employeeTax.col.employee">Employee</TableCell>
                                    <TableCell data-help="tax.employeeTax.col.income" align="right">Taxable Income YTD</TableCell>
                                    <TableCell data-help="tax.employeeTax.col.deducted" align="right">Tax Deducted YTD</TableCell>
                                    <TableCell data-help="tax.employeeTax.col.month" align="right">This Month Tax</TableCell>
                                    <TableCell data-help="tax.employeeTax.col.projected" align="right">Projected Remaining</TableCell>
                                    <TableCell data-help="tax.employeeTax.col.certificate" align="center">Certificate</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {summaries.map(s => (
                                    <TableRow key={s.userId}>
                                        <TableCell>{s.userName}</TableCell>
                                        <TableCell align="right">{s.taxableIncomeYtd.toLocaleString()}</TableCell>
                                        <TableCell align="right">
                                            <Typography color="primary.main" fontWeight={600}>
                                                {s.taxDeductedYtd.toLocaleString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">{s.taxThisMonth.toLocaleString()}</TableCell>
                                        <TableCell align="right">
                                            <Typography color="warning.main">{s.projectedRemainingTax.toLocaleString()}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Button data-help="tax.employeeTax.action.certificate" size="small" onClick={() => downloadCertificate(s.userId)}>
                                                PDF
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </ScrollableTable>
                </Paper>
            )}
        </Box>
    );
};

export default EmployeeTaxList;
